import { cloneDeep, isArray, merge, mergeWith } from 'lodash'
import { StateCreator } from 'zustand'

import { EOnboardingStepFriendlyNames } from '~/features/onboarding/models'
import { defaultChatBotLayout } from '~/features/settings/components/chatBot/utils'
import {
  ESettingsStepFriendlyNames,
  TChatbotSettingsDetails,
  TChatbotSettingsLayout,
  TSettingsDefinitionSteps,
} from '~/features/settings/models'
import EStoreStateKeys from '~/models/store'
import { DeepPartial } from '~/utils/misc'

type TSettingsDefinition = {
  steps: Partial<TSettingsDefinitionSteps>
}

type TSettings = {
  [EStoreStateKeys.settings]: TSettingsDefinition
}

export type TSettingsState = TSettings & {
  setSettings(settings: Partial<TSettingsDefinition>): void
  setChatbotSettingsLayout(
    chatBotLayout: DeepPartial<TChatbotSettingsLayout>,
  ): void
  setChatbotSettingsDetails(
    chatBotDetails: Partial<TChatbotSettingsDetails>,
  ): void
  resetSettings(): void
}

export const initialSettingsState: TSettingsDefinition = {
  steps: {
    [ESettingsStepFriendlyNames.basicInfo]: null,
    [EOnboardingStepFriendlyNames.creatingLibby]: false,
    [ESettingsStepFriendlyNames.convoOptions]: null,
    [ESettingsStepFriendlyNames.knowledge]: null,
    [ESettingsStepFriendlyNames.notifications]: null,
    [ESettingsStepFriendlyNames.chatbot]: {
      layout: cloneDeep(defaultChatBotLayout),
      details: null,
    },
    [ESettingsStepFriendlyNames.email]: null,
    [ESettingsStepFriendlyNames.phoneText]: null,
  },
}

const createSettingsSlice: StateCreator<
  TSettingsState,
  [['zustand/devtools', unknown]],
  []
> = (set) => ({
  settings: cloneDeep(initialSettingsState),
  setSettings: (settings) =>
    set(
      (state) => ({
        settings: mergeWith(
          cloneDeep(state.settings),
          settings,
          (objValue, srcValue) => {
            if (isArray(objValue)) {
              return srcValue
            }
            // eslint-disable-next-line consistent-return
            return undefined
          },
        ),
      }),
      undefined,
      {
        type: 'setSettings',
        settings,
      },
    ),

  setChatbotSettingsLayout: (chatbotLayout) =>
    set(
      (state) => ({
        settings: cloneDeep(
          merge(state.settings, {
            steps: {
              [ESettingsStepFriendlyNames.chatbot]: { layout: chatbotLayout },
            },
          }),
        ),
      }),
      undefined,
      {
        type: 'setChatbotSettingsLayout',
        chatbotLayout,
      },
    ),
  setChatbotSettingsDetails: (chatbotDetails) =>
    set(
      (state) => ({
        settings: cloneDeep(
          merge(state.settings, {
            steps: {
              [ESettingsStepFriendlyNames.chatbot]: { details: chatbotDetails },
            },
          }),
        ),
      }),
      undefined,
      {
        type: 'setChatbotSettingsDetails',
        chatbotDetails,
      },
    ),
  resetSettings: () =>
    set(
      () => ({
        settings: cloneDeep(initialSettingsState),
      }),
      undefined,
      {
        type: 'resetSettings',
      },
    ),
})

export default createSettingsSlice
